import React, { useState, useEffect, useContext } from 'react';
import ReactGA from 'react-ga';
import PrivacyTermsLayout, { FinishScreenFields } from './privacy-terms-layout';
import {
    useSetResponsibleInformationMutation,
    useTrackClientIpMutation,
    CnpjErrorReason,
    useNotifyLoanApplicationCreatedMutation,
    LoanApplicationInput,
    useSetNotifyViaWhatsappMutation,
    useGetLoanQuery,
    useCanApplyForLoanLazyQuery
} from 'generated/graphql';
import CnpjValidationErrorMessage, {
    CnpjValidationErrorMessageProps,
} from '../error-message/cnpj-validation-error-message';
import { nextStep } from 'actions/step.action';
import { registerConversion, registerTagmanagerEvent, TagManagerEvent } from 'configs/trackers';
import { BreadcrumbsSession, calculateFingerPrint } from 'utils/utils';
import { ProfileContext } from 'providers/profile.provider';
import {  useNavigation, useNeuroId } from 'hooks/index';
import { LoanApplicationContext } from 'containers/context/useGetLoanApplication';
import Loading from 'components/loading';

const PrivacyTerms: React.FC = () => {
    const [setResponsibleInformation] = useSetResponsibleInformationMutation();
    const [errorProps, setErrorProps] = useState<
        CnpjValidationErrorMessageProps
    >(null);
    const [loading, setLoading] = useState(false);
    const [fingerPrint, setFingerPrint] = useState('');
    const { nextScreen, previousScreen, reset } = useNavigation();
    const [notifyLoanCreated] = useNotifyLoanApplicationCreatedMutation();
    const [trackClientIp] = useTrackClientIpMutation();
    const [setNotifyAsWhatsapp] = useSetNotifyViaWhatsappMutation();
    const { loanApplicationId } = useContext(LoanApplicationContext);
    const {
        steps: { privacyTerms },
        profile,
    } = useContext(ProfileContext);
    const { setUserId, setCheckpoint, setSubmit } = useNeuroId();
    const [canApplyForLoanQuery] = useCanApplyForLoanLazyQuery();

    const { data, loading: loanLoanding } = useGetLoanQuery({
        variables:{
            id:loanApplicationId
        },
        fetchPolicy:"network-only"
    });

    useEffect(() => {
        if(!loanApplicationId) {
            reset();
        }
        setUserId(loanApplicationId);
    },[loanApplicationId])

    useEffect(() => {
        calculateFingerPrint(setFingerPrint);

        ReactGA.event({
            category: 'Navegação Direto',
            action: 'Exibição',
            label: 'Exibição /pedido/termos-de-uso',
            nonInteraction: true,
        });

        setCheckpoint('ApplicationComplete');
    }, []);

    const onSubmit = async (values: FinishScreenFields) => {
        setSubmit();
        const {
            responsibleName,
            email,
            responsibleDocument,
            companyDocument,
            phone,
        } = data?.loanApplication?.borrower;

        setLoading(true);

        trackClientIp({ variables: { loanApplicationId: data.loanApplication.id } });

        if (values.email !== email) {
            await setResponsibleInformation({
                variables: {
                    loanApplicationId: data.loanApplication.id,
                    borrower: {
                        responsibleName,
                        responsibleDocument,
                        phone,
                        email: values.email,
                    },
                },
            });
        }

        if (values.notifyViaWhatsapp === true) {
            setNotifyAsWhatsapp({
                variables: {
                    loanApplicationId: data.loanApplication.id,
                    notifyViaWhatsapp: values.notifyViaWhatsapp,
                }
            })
        }

        try {
            const response = await canApplyForLoanQuery({
                variables:{
                    cnpj: companyDocument
                }
            })

            if (response.data?.canApplyForLoan.result)
            {
                await startAnalysis();
                registerConversion(data.loanApplication);

                BreadcrumbsSession.reset();
                nextStep();
                nextScreen();
                registerTagmanagerEvent(TagManagerEvent.CONFIRMATION_PROFILE);
            } else
            {
                setErrorProps({
                    cnpjErrorReason: response.data?.canApplyForLoan.cnpjErrorReason,
                    daysLeft: response.data?.canApplyForLoan.daysLeft
                });
            }
        } catch (error) {
            setErrorProps({ cnpjErrorReason: CnpjErrorReason.Unknow });
        }
    };

    const startAnalysis = async () => {
        const {
            id,
            amountRequested,
            reason,
            reasonDetail,
            borrower: {
                responsibleName,
                responsibleDocument,
                email,
                phone,
                companyDocument,
                monthlyRevenue,
                relationship,
            },
            tracking: { trackers },
        } = data && data.loanApplication;

        const loanApplicationInput: LoanApplicationInput = {
            id,
            amountRequested,
            reason,
            reasonDetail,
            borrower: {
                responsibleName,
                responsibleDocument,
                email,
                phone,
                companyDocument,
                monthlyRevenue,
                relationship,
            },
            tracking: {
                trackers
            },
        };

        await notifyLoanCreated({ variables: { loanApplication:loanApplicationInput } });
    };

    if (errorProps) {
        return <CnpjValidationErrorMessage {...errorProps} />;
    }

    const termFields =
        profile === 'default'
            ? {
                agreeTerms: false,
                authorizeInformationAccess: false,
                notifyViaWhatsapp: false,
            }
            : {
                agreeTerms: false,
                authorizeInformationAccess: false,
                notifyViaWhatsapp: false,
                partnerTerms: false,
            };

    if(loanLoanding && !data)
    {
        return(
            <Loading />
        );
    }

    return (
        <PrivacyTermsLayout
            initialValue={{
                ...termFields,
                responsibleName: data?.loanApplication.borrower.responsibleName,
                email: data?.loanApplication.borrower.email
            }}
            stepInfo={privacyTerms}
            screenProfile={profile}
            loading={loading || fingerPrint === ''}
            onBack={previousScreen}
            onSubmit={onSubmit}
        />
    );
};

export default PrivacyTerms;
